import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    fetchPacientes, 
    fetchCertificadoVisualPaciente, 
    saveCertificadoVisual, 
    enviarCertificadoVisualCorreo 
} from '../../servicios/apiClinica';

const DEFAULT_DOC = {
    datos_paciente: {
        nombre_completo: '',
        sexo: '',
        identificacion: '',
        fecha: '',
        edad: '',
        telefono: '',
        email: ''
    },
    agudeza_visual: {
        od: { av_lejos: '', lente_iol: '', av_l_ph: '', av_cerca: '', optotipo: 'LETRAS' },
        oi: { av_lejos: '', lente_iol: '', av_l_ph: '', av_cerca: '', optotipo: '' }
    },
    examen_refraccion: {
        od: {
            autorefrac_esf: '', autorefrac_cil: '', autorefrac_eje: '',
            retino_esf: '', retino_cil: '', retino_eje: '', retino_av: '',
            querato_k1: '', querato_k2: '', querato_eje_k1: '', querato_eje_k2: ''
        },
        oi: {
            autorefrac_esf: '', autorefrac_cil: '', autorefrac_eje: '',
            retino_esf: '', retino_cil: '', retino_eje: '', retino_av: '',
            querato_k1: '', querato_k2: '', querato_eje_k1: '', querato_eje_k2: ''
        }
    },
    prescripcion_final: {
        od: {
            lejos_esf: '', lejos_cil: '', lejos_eje: '', lejos_av: '',
            adicion: '',
            cerca_esf: '', cerca_cil: '', cerca_eje: '', cerca_av: '',
            observaciones: ''
        },
        oi: {
            lejos_esf: '', lejos_cil: '', lejos_eje: '', lejos_av: '',
            adicion: '',
            cerca_esf: '', cerca_cil: '', cerca_eje: '', cerca_av: '',
            observaciones: ''
        }
    },
    dp_prisma: {
        od: {
            naso_pupilar: '', altura_pupilar: '', dp_lejos: '', dp_cerca: '',
            altura_bifocal: '', prisma_horizontal: '', prisma_vertical: ''
        },
        oi: {
            naso_pupilar: '', altura_pupilar: '', dp_lejos: '', dp_cerca: '',
            altura_bifocal: '', prisma_horizontal: '', prisma_vertical: ''
        },
        observaciones: '',
        control: ''
    },
    control_profesional: {
        proximo_control: '',
        nombre_profesional: '',
        registro_profesional: '',
        ci_firma_sello: ''
    }
};

const CertificadoVisual = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const docPrintRef = useRef(null);

    const [pacientes, setPacientes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
    const [mostrarMenuBusqueda, setMostrarMenuBusqueda] = useState(false);

    const [certificadoId, setCertificadoId] = useState(null);
    const [docData, setDocData] = useState(DEFAULT_DOC);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [estadoEnvio, setEstadoEnvio] = useState(null);

    // Modal de envío de correo
    const [modalCorreoAbierto, setModalCorreoAbierto] = useState(false);
    const [correoDestino, setCorreoDestino] = useState('');
    const [asuntoCorreo, setAsuntoCorreo] = useState('');
    const [mensajeExtra, setMensajeExtra] = useState('');
    const [enviandoCorreo, setEnviandoCorreo] = useState(false);

    // Mensajes Toast / Alerta
    const [alerta, setAlerta] = useState(null);

    const mostrarAlerta = (tipo, mensaje) => {
        setAlerta({ tipo, mensaje });
        setTimeout(() => setAlerta(null), 5000);
    };

    // Cargar pacientes
    useEffect(() => {
        fetchPacientes().then(data => setPacientes(data || [])).catch(() => setPacientes([]));
    }, []);

    // Cargar paciente por query param
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const pId = params.get('patient_id') || params.get('patient');
        if (pId && pacientes.length > 0) {
            const enc = pacientes.find(p => String(p.id) === String(pId));
            if (enc) {
                seleccionarPaciente(enc);
            }
        }
    }, [location.search, pacientes]);

    const pacientesFiltrados = useMemo(() => {
        if (!busqueda.trim()) return pacientes.slice(0, 10);
        const q = busqueda.toLowerCase().trim();
        return pacientes.filter(p => 
            (p.first_name || '').toLowerCase().includes(q) ||
            (p.last_name || '').toLowerCase().includes(q) ||
            (p.cedula || '').includes(q)
        ).slice(0, 15);
    }, [pacientes, busqueda]);

    const asegurarSexo = (datos, p) => {
        if (!datos) return datos;
        const copia = JSON.parse(JSON.stringify(datos));
        if (!copia.datos_paciente) copia.datos_paciente = {};
        if (!copia.datos_paciente.sexo) {
            const nombre = (copia.datos_paciente.nombre_completo || p.first_name || '').toUpperCase();
            const nombresFem = ['GABRIELA', 'MARIA', 'INGRID', 'DANNA', 'ANA', 'DANIELA', 'VALERIA', 'CARMEN', 'PAOLA', 'SOFIA', 'DIANA', 'LUCIA', 'ELIZABETH', 'JESSICA', 'FERNANDA', 'ANDREA', 'CAROLINA', 'PATRICIA', 'GLORIA', 'MONICA', 'SANDRA', 'XIMENA', 'VERONICA', 'WENDY', 'YADIRA', 'JOHANNA', 'BELEN', 'CAMILA', 'MELISSA'];
            const esFem = (p.gender && p.gender.toUpperCase().startsWith('F')) || nombresFem.some(n => nombre.split(' ').includes(n)) || (nombre.endsWith('A') && !nombre.endsWith('IA'));
            copia.datos_paciente.sexo = esFem ? 'F' : 'M';
        }
        return copia;
    };

    const seleccionarPaciente = async (p) => {
        setPacienteSeleccionado(p);
        setBusqueda(`${p.first_name || ''} ${p.last_name || ''}`.trim());
        setMostrarMenuBusqueda(false);
        setCargando(true);
        try {
            const res = await fetchCertificadoVisualPaciente(p.id);
            if (res.existe && res.certificado) {
                setCertificadoId(res.certificado.id);
                setDocData(asegurarSexo(res.certificado.datos || DEFAULT_DOC, p));
                setEstadoEnvio({
                    enviado_correo: res.certificado.enviado_correo,
                    ultimo_envio: res.certificado.ultimo_envio,
                    correo_destinatario: res.certificado.correo_destinatario
                });
                mostrarAlerta('info', 'Se cargó el certificado visual guardado de este paciente.');
            } else {
                setCertificadoId(null);
                setDocData(asegurarSexo(res.datos_precargados || DEFAULT_DOC, p));
                setEstadoEnvio(null);
                mostrarAlerta('success', 'Datos del paciente e historia visual precargados con éxito.');
            }
        } catch (error) {
            console.error('Error cargando certificado:', error);
            mostrarAlerta('danger', 'Error al consultar datos clínicos del paciente.');
        } finally {
            setCargando(false);
        }
    };

    // Manejo de cambios en el documento
    const updateField = (seccion, campo, valor, subseccion = null) => {
        setDocData(prev => {
            const copia = { ...prev };
            if (subseccion) {
                copia[seccion] = {
                    ...copia[seccion],
                    [subseccion]: {
                        ...copia[seccion]?.[subseccion],
                        [campo]: valor
                    }
                };
            } else {
                copia[seccion] = {
                    ...copia[seccion],
                    [campo]: valor
                };
            }
            return copia;
        });
    };

    // Guardar en la base de datos
    const handleGuardar = async () => {
        if (!pacienteSeleccionado) {
            mostrarAlerta('warning', 'Seleccione un paciente antes de guardar.');
            return;
        }
        setGuardando(true);
        try {
            const payload = {
                id: certificadoId || undefined,
                patient: pacienteSeleccionado.id,
                fecha: new Date().toISOString().split('T')[0],
                datos: docData
            };
            const guardado = await saveCertificadoVisual(payload);
            setCertificadoId(guardado.id);
            mostrarAlerta('success', 'Certificado visual guardado con éxito. Queda almacenado exclusivamente en el módulo de Certificados sin alterar otros historiales.');
        } catch (error) {
            console.error('Error al guardar certificado:', error);
            mostrarAlerta('danger', error.message || 'Error al guardar el certificado visual.');
        } finally {
            setGuardando(false);
        }
    };

    // Clonador y preparador de DOM para exportación limpia a PDF
    // Reemplaza inputs por texto real, evitando recortes, desfases y distorsiones de html2canvas
    const prepararElementoPDF = () => {
        if (!docPrintRef.current) return null;

        const clone = docPrintRef.current.cloneNode(true);

        // 1. Reemplazar inputs y textareas por texto plano en el clon
        const inputs = clone.querySelectorAll('input, textarea');
        inputs.forEach(inp => {
            const val = (inp.value || '').trim();
            const parentTd = inp.closest('td');
            const parentDiv = inp.closest('div');

            if (parentDiv && parentDiv.style.display === 'flex') {
                parentDiv.style.display = 'block';
                parentDiv.style.whiteSpace = 'normal';
                const textSpan = document.createElement('span');
                textSpan.textContent = val ? ` ${val}` : '';
                textSpan.style.fontWeight = 'bold';
                textSpan.style.fontSize = '9.5px';
                textSpan.style.color = '#000000';
                textSpan.style.wordBreak = 'break-word';
                inp.parentNode.replaceChild(textSpan, inp);
            } else if (parentTd) {
                const span = document.createElement('span');
                span.textContent = val;
                span.style.fontFamily = 'Arial, Helvetica, sans-serif';
                span.style.fontSize = '9.5px';
                span.style.fontWeight = (inp.style.fontWeight === 'bold' || parentTd.classList.contains('bg-yellow')) ? 'bold' : 'normal';
                span.style.textAlign = inp.style.textAlign || 'center';
                span.style.display = 'block';
                span.style.width = '100%';
                span.style.lineHeight = '1.25';
                span.style.whiteSpace = 'normal';
                span.style.wordBreak = 'break-word';
                span.style.color = '#000000';
                inp.parentNode.replaceChild(span, inp);
            } else {
                const textNode = document.createTextNode(val);
                inp.parentNode.replaceChild(textNode, inp);
            }
        });

        // 2. Celdas de tablas con altura y relleno exacto
        clone.querySelectorAll('th, td').forEach(c => {
            c.style.overflow = 'visible';
            c.style.padding = '4px 2px';
            c.style.lineHeight = '1.25';
            c.style.verticalAlign = 'middle';
            c.style.boxSizing = 'border-box';
            c.style.wordBreak = 'break-word';
        });

        // 3. Dimensiones exactas de hoja A4
        clone.style.width = '780px';
        clone.style.maxWidth = '780px';
        clone.style.minWidth = '780px';
        clone.style.margin = '0 auto';
        clone.style.padding = '14px 18px';
        clone.style.boxShadow = 'none';
        clone.style.border = 'none';
        clone.style.background = '#ffffff';
        clone.style.boxSizing = 'border-box';

        // 4. Montar fuera de pantalla
        const tempContainer = document.createElement('div');
        tempContainer.id = 'pdf-export-temp-container';
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-10000px';
        tempContainer.style.top = '0';
        tempContainer.style.width = '780px';
        tempContainer.style.background = '#ffffff';
        tempContainer.style.zIndex = '-9999';
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        return {
            element: clone,
            limpiar: () => {
                if (tempContainer.parentNode) {
                    tempContainer.parentNode.removeChild(tempContainer);
                }
            }
        };
    };

    // Genera el PDF con html2canvas + jsPDF: la captura se escala al ancho útil
    // de la hoja A4 y se centra (html2pdf recortaba el lado derecho del certificado)
    const generarPDF = async (escala = 2, calidad = 0.98) => {
        const prep = prepararElementoPDF();
        if (!prep) throw new Error('No se pudo preparar el documento.');
        try {
            const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
                import('html2canvas'),
                import('jspdf')
            ]);
            const canvas = await html2canvas(prep.element, {
                scale: escala,
                useCORS: true,
                backgroundColor: '#ffffff',
                scrollX: 0,
                scrollY: 0,
                windowWidth: 780,
                logging: false
            });

            const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
            const margen = 6;
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            let w = pageW - margen * 2;
            let h = (canvas.height * w) / canvas.width;
            if (h > pageH - margen * 2) {
                h = pageH - margen * 2;
                w = (canvas.width * h) / canvas.height;
            }
            pdf.addImage(canvas.toDataURL('image/jpeg', calidad), 'JPEG', (pageW - w) / 2, margen, w, h);
            return pdf;
        } finally {
            prep.limpiar();
        }
    };

    const nombreArchivoPDF = () => {
        const nombre = docData.datos_paciente?.nombre_completo || pacienteSeleccionado?.first_name || 'Paciente';
        return `Certificado_Visual_${nombre.replace(/[^a-zA-Z0-9_\-]/g, '_')}.pdf`;
    };

    // Descargar PDF
    const handleDescargarPDF = async () => {
        if (!docPrintRef.current) return;
        try {
            mostrarAlerta('info', 'Generando archivo PDF en alta calidad...');
            const pdf = await generarPDF(2, 0.98);
            pdf.save(nombreArchivoPDF());
            mostrarAlerta('success', 'PDF descargado nítidamente y sin distorsiones.');
        } catch (error) {
            console.error('Error al generar PDF:', error);
            mostrarAlerta('danger', 'No se pudo generar el PDF descargable.');
        }
    };

    // Imprimir directamente
    const handleImprimir = () => {
        window.print();
    };

    // Abrir modal de envío por correo
    const handleAbrirModalCorreo = () => {
        if (!pacienteSeleccionado) {
            mostrarAlerta('warning', 'Seleccione primero un paciente.');
            return;
        }
        const emailPac = docData.datos_paciente?.email || pacienteSeleccionado.email || '';
        setCorreoDestino(emailPac);
        setAsuntoCorreo(`Certificado de Salud Visual - ${docData.datos_paciente?.nombre_completo || 'Visión Click'}`);
        setMensajeExtra('');
        setModalCorreoAbierto(true);
    };

    // Ejecutar envío por correo
    const handleConfirmarEnvioCorreo = async (e) => {
        e.preventDefault();
        if (!correoDestino.trim()) {
            mostrarAlerta('warning', 'Por favor ingrese un correo electrónico de destino.');
            return;
        }

        setEnviandoCorreo(true);
        try {
            mostrarAlerta('info', 'Generando documento oficial en PDF...');
            const pdf = await generarPDF(1.5, 0.9);
            const pdfDataUri = pdf.output('datauristring', { filename: nombreArchivoPDF() });

            if (!pdfDataUri || !pdfDataUri.includes('base64')) {
                throw new Error('No se pudo generar el archivo PDF adjunto.');
            }

            mostrarAlerta('info', 'Enviando certificado por correo electrónico...');

            const resp = await enviarCertificadoVisualCorreo({
                patient_id: pacienteSeleccionado.id,
                email: correoDestino.trim(),
                nombre_paciente: docData.datos_paciente?.nombre_completo || `${pacienteSeleccionado.first_name} ${pacienteSeleccionado.last_name}`,
                pdf_base64: pdfDataUri,
                asunto: asuntoCorreo.trim(),
                mensaje: mensajeExtra.trim()
            });

            setEstadoEnvio({
                enviado_correo: true,
                ultimo_envio: new Date().toISOString(),
                correo_destinatario: correoDestino.trim()
            });

            if (!certificadoId) {
                try {
                    await handleGuardar();
                } catch (ignore) {}
            }

            setModalCorreoAbierto(false);
            mostrarAlerta('success', resp.mensaje || `Certificado visual enviado con éxito a ${correoDestino}`);
        } catch (error) {
            console.error('Error al enviar correo:', error);
            mostrarAlerta('danger', error.message || 'Error al enviar el certificado por correo.');
        } finally {
            setEnviandoCorreo(false);
        }
    };

    const pData = docData.datos_paciente || {};
    const av = docData.agudeza_visual || { od: {}, oi: {} };
    const refrac = docData.examen_refraccion || { od: {}, oi: {} };
    const rx = docData.prescripcion_final || { od: {}, oi: {} };
    const dp = docData.dp_prisma || { od: {}, oi: {} };
    const prof = docData.control_profesional || {};

    return (
        <div className="container-fluid py-3 px-2 px-md-4" style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <style>{`
                .cert-topbar {
                    background: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    padding: 12px 18px;
                    margin: 0 auto 18px;
                    max-width: 900px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .cert-search-box {
                    position: relative;
                    width: 340px;
                    max-width: 100%;
                }
                .cert-search-box input {
                    width: 100%;
                    padding: 8px 12px 8px 34px;
                    border-radius: 8px;
                    border: 1.5px solid #cbd5e1;
                    font-size: 0.88rem;
                    outline: none;
                }
                .cert-search-box input:focus {
                    border-color: #0f172a;
                }
                .cert-search-dropdown {
                    position: absolute;
                    left: 0;
                    right: 0;
                    top: calc(100% + 4px);
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.12);
                    z-index: 100;
                    max-height: 280px;
                    overflow-y: auto;
                }
                .cert-search-item {
                    padding: 8px 12px;
                    cursor: pointer;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .cert-search-item:hover {
                    background: #f8fafc;
                }
                .cert-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 7px 14px;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 0.85rem;
                    cursor: pointer;
                    border: none;
                    transition: all 0.2s;
                }
                .cert-btn-primary {
                    background: #065f46;
                    color: #ffffff;
                }
                .cert-btn-primary:hover {
                    background: #047857;
                }
                .cert-btn-dark {
                    background: #111827;
                    color: #ffffff;
                }
                .cert-btn-dark:hover {
                    background: #1f2937;
                }
                .cert-btn-outline {
                    background: #ffffff;
                    color: #1e293b;
                    border: 1.5px solid #cbd5e1;
                }
                .cert-btn-outline:hover {
                    background: #f1f5f9;
                }
                .cert-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                /* ── Estilos de la Hoja del Certificado Oficial ── */
                .cert-paper-container {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    overflow-x: auto;
                    padding: 0 8px 40px;
                    box-sizing: border-box;
                }
                .cert-paper {
                    background: #ffffff;
                    width: 100%;
                    max-width: 860px;
                    min-width: 800px;
                    margin: 0 auto;
                    padding: 18px 22px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-family: Arial, Helvetica, sans-serif;
                    color: #000000;
                    box-sizing: border-box;
                }
                .cert-paper table {
                    width: 100%;
                    table-layout: fixed;
                    border-collapse: collapse;
                    margin-bottom: 6px;
                    font-size: 9.5px;
                }
                .cert-paper th, .cert-paper td {
                    border: 1px solid #000000;
                    padding: 3px 2px;
                    vertical-align: middle;
                    text-align: center;
                    box-sizing: border-box;
                    line-height: 1.25;
                }
                .cert-paper th.bg-yellow, .cert-paper td.bg-yellow {
                    background-color: #ffff99 !important;
                    font-weight: bold;
                    color: #000000;
                }
                .cert-paper .sec-title {
                    font-size: 10px;
                    font-weight: bold;
                    margin: 4px 0 2px;
                    color: #000000;
                    text-transform: uppercase;
                    text-align: left;
                }
                .cert-paper input {
                    width: 100%;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-family: inherit;
                    font-size: inherit;
                    color: inherit;
                    text-align: center;
                    padding: 1px 2px;
                    line-height: 1.25;
                    box-sizing: border-box;
                    display: block;
                }
                .cert-paper input:focus {
                    background: #fef08a;
                    border-radius: 2px;
                }
                .cert-divider {
                    border: none;
                    border-top: 1.5px solid #000000;
                    margin: 6px 0;
                }
                .cert-footer-info {
                    font-size: 8.5px;
                    line-height: 1.4;
                    color: #111827;
                    margin-top: 6px;
                    text-align: left;
                }

                /* ── Modal de envío por correo ── */
                .cv-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                    z-index: 10050;
                }
                .cv-modal-content {
                    background: #ffffff;
                    width: 100%;
                    max-width: 500px;
                    max-height: calc(100vh - 32px);
                    overflow-y: auto;
                    border-radius: 16px;
                    box-shadow: 0 20px 50px rgba(0,0,0,0.3);
                }
                .cv-modal-header {
                    background: #111827;
                    color: #ffffff;
                    padding: 14px 18px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-radius: 16px 16px 0 0;
                }
                .cv-modal-header h5 {
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 700;
                }
                .cv-modal-close {
                    background: transparent;
                    border: none;
                    color: #ffffff;
                    font-size: 1rem;
                    cursor: pointer;
                    padding: 4px;
                }
                .cv-modal-body {
                    padding: 20px;
                }
                .cv-modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 8px;
                    padding: 14px 18px;
                    background: #f8fafc;
                    border-radius: 0 0 16px 16px;
                }

                /* ── Formato de Impresión / PDF ── */
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .cert-paper, .cert-paper * {
                        visibility: visible !important;
                    }
                    .cert-paper {
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 6mm 8mm !important;
                        box-shadow: none !important;
                        border: none !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    @page {
                        size: A4 portrait;
                        margin: 6mm;
                    }
                    .d-print-none, .cert-topbar {
                        display: none !important;
                    }
                    .cert-paper input {
                        border: none !important;
                        background: transparent !important;
                    }
                }
            `}</style>

            {/* ── Alertas ── */}
            {alerta && (
                <div style={{ maxWidth: 900, margin: '0 auto 14px' }}>
                    <div className={`alert alert-${alerta.tipo} d-flex align-items-center justify-content-between py-2 px-3 rounded-3 shadow-sm mb-0`}>
                        <div className="d-flex align-items-center gap-2">
                            <i className={`bi ${alerta.tipo === 'success' ? 'bi-check-circle-fill' : alerta.tipo === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'}`}></i>
                            <span>{alerta.mensaje}</span>
                        </div>
                        <button type="button" className="btn-close" onClick={() => setAlerta(null)} style={{ fontSize: '0.75rem' }}></button>
                    </div>
                </div>
            )}

            {/* ── Barra Superior de Acciones y Búsqueda ── */}
            <div className="cert-topbar d-print-none">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Buscador de Paciente */}
                    <div className="cert-search-box">
                        <i className="bi bi-search position-absolute" style={{ left: 11, top: 10, color: '#64748b' }}></i>
                        <input
                            type="text"
                            placeholder="Buscar paciente por nombre o cédula..."
                            value={busqueda}
                            onChange={e => {
                                setBusqueda(e.target.value);
                                setMostrarMenuBusqueda(true);
                            }}
                            onFocus={() => setMostrarMenuBusqueda(true)}
                        />
                        {mostrarMenuBusqueda && pacientesFiltrados.length > 0 && (
                            <div className="cert-search-dropdown">
                                {pacientesFiltrados.map(p => (
                                    <div 
                                        key={p.id} 
                                        className="cert-search-item"
                                        onClick={() => seleccionarPaciente(p)}
                                    >
                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                            {p.first_name || ''} {p.last_name || ''}
                                        </div>
                                        <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
                                            CI: {p.cedula || 'Sin cédula'} · Tel: {p.phone || 'Sin teléfono'} {p.email ? `· ${p.email}` : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {pacienteSeleccionado && (
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="badge bg-dark py-2 px-3 rounded-pill" style={{ fontSize: '0.82rem' }}>
                                <i className="bi bi-person-fill me-1"></i>
                                {pacienteSeleccionado.first_name} {pacienteSeleccionado.last_name}
                            </span>
                            {certificadoId ? (
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle py-2 px-3 rounded-pill" style={{ fontSize: '0.78rem' }}>
                                    <i className="bi bi-file-earmark-check-fill me-1"></i>
                                    Certificado Guardado en Sistema
                                </span>
                            ) : (
                                <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle py-2 px-3 rounded-pill" style={{ fontSize: '0.78rem' }}>
                                    <i className="bi bi-magic me-1"></i>
                                    Precargado desde Historia Visual
                                </span>
                            )}
                            {estadoEnvio?.enviado_correo && (
                                <span className="badge bg-success-subtle text-success border border-success-subtle py-2 px-3 rounded-pill" style={{ fontSize: '0.78rem' }}>
                                    <i className="bi bi-check-circle-fill me-1"></i>
                                    Enviado a {estadoEnvio.correo_destinatario || pacienteSeleccionado.email}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Acciones */}
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <button 
                        type="button" 
                        className="cert-btn cert-btn-dark"
                        onClick={handleGuardar}
                        disabled={guardando || !pacienteSeleccionado}
                        title="Guardar certificado de este paciente"
                    >
                        <i className={`bi ${guardando ? 'bi-hourglass-split' : 'bi-save'}`}></i>
                        {guardando ? 'Guardando...' : 'Guardar'}
                    </button>

                    <button 
                        type="button" 
                        className="cert-btn cert-btn-outline"
                        onClick={handleDescargarPDF}
                        disabled={!pacienteSeleccionado}
                        title="Descargar en PDF"
                    >
                        <i className="bi bi-file-earmark-pdf text-danger"></i>
                        Descargar PDF
                    </button>

                    <button 
                        type="button" 
                        className="cert-btn cert-btn-outline"
                        onClick={handleImprimir}
                        disabled={!pacienteSeleccionado}
                        title="Imprimir certificado"
                    >
                        <i className="bi bi-printer"></i>
                        Imprimir
                    </button>

                    <button 
                        type="button" 
                        className="cert-btn cert-btn-primary"
                        onClick={handleAbrirModalCorreo}
                        disabled={!pacienteSeleccionado}
                        title="Enviar certificado por correo al paciente con PDF adjunto"
                    >
                        <i className="bi bi-envelope-fill"></i>
                        Enviar por Correo
                    </button>
                </div>
            </div>

            {/* Aviso si no hay paciente seleccionado */}
            {!pacienteSeleccionado && (
                <div className="text-center py-4 text-muted d-print-none" style={{ maxWidth: 860, margin: '0 auto' }}>
                    <i className="bi bi-person-lines-fill fs-2 d-block mb-2 text-secondary"></i>
                    <h6 className="fw-bold text-dark">Seleccione un paciente en la barra superior</h6>
                    <p style={{ fontSize: '0.85rem' }}>
                        Busque por nombre o cédula para cargar automáticamente su certificado con todos los datos clínicos de su última consulta.
                    </p>
                </div>
            )}

            {/* ── Hoja Oficial del Certificado Visual (A4) ── */}
            <div className="cert-paper-container">
                <div className="cert-paper" ref={docPrintRef} id="certificado-visual-doc">
                    
                    {/* Encabezado: Logo y Título Oficial */}
                    <div style={{ textAlign: 'center', marginBottom: 4 }}>
                        <img 
                            src="/logo-certificado.png" 
                            alt="Visión Click" 
                            style={{ maxHeight: '72px', objectFit: 'contain', display: 'inline-block' }} 
                        />
                        <div style={{ fontSize: '13.5px', fontWeight: 'bold', letterSpacing: '0.8px', marginTop: 3 }}>
                            INFORME OPTOMÉTRICO
                        </div>
                    </div>

                    <hr className="cert-divider" />

                    {/* 1. DATOS DEL PACIENTE */}
                    <div className="sec-title">DATOS DEL PACIENTE</div>
                    <table style={{ marginBottom: 6 }}>
                        <colgroup>
                            <col style={{ width: '43%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '20%' }} />
                        </colgroup>
                        <tbody>
                            <tr>
                                <td className="bg-yellow" style={{ textAlign: 'left', padding: '2px 5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                        <strong>NOMBRE COMPLETO:</strong>
                                        <input 
                                            value={pData.nombre_completo || ''} 
                                            onChange={e => updateField('datos_paciente', 'nombre_completo', e.target.value)} 
                                            placeholder="Nombre completo..."
                                            style={{ flex: 1, minWidth: 0, textAlign: 'left', fontWeight: 'bold' }}
                                        />
                                    </div>
                                </td>
                                <td className="bg-yellow" style={{ textAlign: 'left', padding: '2px 5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                        <strong>SEXO:</strong>
                                        <input 
                                            value={pData.sexo || ''} 
                                            onChange={e => updateField('datos_paciente', 'sexo', e.target.value)} 
                                            placeholder="M / F"
                                            style={{ width: '30px', textAlign: 'center', fontWeight: 'bold' }}
                                        />
                                    </div>
                                </td>
                                <td className="bg-yellow" style={{ textAlign: 'left', padding: '2px 5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                        <strong>IDENTIFICACIÓN:</strong>
                                        <input 
                                            value={pData.identificacion || ''} 
                                            onChange={e => updateField('datos_paciente', 'identificacion', e.target.value)} 
                                            placeholder="C.I. / Pasaporte"
                                            style={{ flex: 1, minWidth: 0, textAlign: 'left', fontWeight: 'bold' }}
                                        />
                                    </div>
                                </td>
                                <td className="bg-yellow" style={{ textAlign: 'left', padding: '2px 5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                        <strong>FECHA:</strong>
                                        <input 
                                            value={pData.fecha || ''} 
                                            onChange={e => updateField('datos_paciente', 'fecha', e.target.value)} 
                                            placeholder="DD/MM/AAAA"
                                            style={{ width: '80px', textAlign: 'center', fontWeight: 'bold' }}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="bg-yellow" style={{ textAlign: 'left', padding: '2px 5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                        <strong>EDAD:</strong>
                                        <input 
                                            value={pData.edad || ''} 
                                            onChange={e => updateField('datos_paciente', 'edad', e.target.value)} 
                                            placeholder="Ej: 20 años"
                                            style={{ width: '80px', textAlign: 'left' }}
                                        />
                                    </div>
                                </td>
                                <td className="bg-yellow" colSpan={2} style={{ textAlign: 'left', padding: '2px 5px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                        <strong>TELÉFONO:</strong>
                                        <input 
                                            value={pData.telefono || ''} 
                                            onChange={e => updateField('datos_paciente', 'telefono', e.target.value)} 
                                            placeholder="09..."
                                            style={{ width: '120px', textAlign: 'left' }}
                                        />
                                    </div>
                                </td>
                                <td className="bg-yellow"></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 2. AGUDEZA VISUAL SIN CORRECCION */}
                    <table>
                        <colgroup>
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '18%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th colSpan={6} className="bg-yellow" style={{ textAlign: 'left', paddingLeft: 6 }}>
                                    AGUDEZA VISUAL SIN CORRECCION
                                </th>
                            </tr>
                            <tr className="bg-yellow">
                                <th>OJOS</th>
                                <th>AV LEJOS</th>
                                <th>LENTE (IOL)</th>
                                <th>AV L PH (PINHOLE)</th>
                                <th>AV CERCA</th>
                                <th>OPTOTIPO</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="bg-yellow">OD</td>
                                <td><input value={av.od?.av_lejos || ''} onChange={e => updateField('agudeza_visual', 'av_lejos', e.target.value, 'od')} placeholder="20/..." /></td>
                                <td><input value={av.od?.lente_iol || ''} onChange={e => updateField('agudeza_visual', 'lente_iol', e.target.value, 'od')} /></td>
                                <td><input value={av.od?.av_l_ph || ''} onChange={e => updateField('agudeza_visual', 'av_l_ph', e.target.value, 'od')} /></td>
                                <td><input value={av.od?.av_cerca || ''} onChange={e => updateField('agudeza_visual', 'av_cerca', e.target.value, 'od')} placeholder="0.50" /></td>
                                <td rowSpan={2} style={{ verticalAlign: 'middle', fontWeight: 'bold' }}>
                                    <input value={av.od?.optotipo || ''} onChange={e => updateField('agudeza_visual', 'optotipo', e.target.value, 'od')} placeholder="LETRAS" />
                                </td>
                            </tr>
                            <tr>
                                <td className="bg-yellow">OI</td>
                                <td><input value={av.oi?.av_lejos || ''} onChange={e => updateField('agudeza_visual', 'av_lejos', e.target.value, 'oi')} placeholder="20/..." /></td>
                                <td><input value={av.oi?.lente_iol || ''} onChange={e => updateField('agudeza_visual', 'lente_iol', e.target.value, 'oi')} /></td>
                                <td><input value={av.oi?.av_l_ph || ''} onChange={e => updateField('agudeza_visual', 'av_l_ph', e.target.value, 'oi')} /></td>
                                <td><input value={av.oi?.av_cerca || ''} onChange={e => updateField('agudeza_visual', 'av_cerca', e.target.value, 'oi')} placeholder="0.50" /></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 3. AUTOREFRACTOMETRO / RETINOSCOPIA / QUERATOMETRIA */}
                    <table>
                        <colgroup>
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '7.5%' }} />
                            <col style={{ width: '7.5%' }} />
                            <col style={{ width: '7.5%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '7.5%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-yellow">
                                <th rowSpan={2}>OJOS</th>
                                <th colSpan={3}>AUTOREFRACTOMETRO</th>
                                <th colSpan={4}>RETINOSCOPIA</th>
                                <th colSpan={4}>QUERATOMETRIA</th>
                            </tr>
                            <tr className="bg-yellow">
                                <th>ESF</th><th>CIL</th><th>EJE</th>
                                <th>ESF</th><th>CIL</th><th>EJE</th><th>AV</th>
                                <th>K1</th><th>K2</th><th>EJE K1</th><th>EJE K2</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="bg-yellow">OD</td>
                                <td><input value={refrac.od?.autorefrac_esf || ''} onChange={e => updateField('examen_refraccion', 'autorefrac_esf', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.autorefrac_cil || ''} onChange={e => updateField('examen_refraccion', 'autorefrac_cil', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.autorefrac_eje || ''} onChange={e => updateField('examen_refraccion', 'autorefrac_eje', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.retino_esf || ''} onChange={e => updateField('examen_refraccion', 'retino_esf', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.retino_cil || ''} onChange={e => updateField('examen_refraccion', 'retino_cil', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.retino_eje || ''} onChange={e => updateField('examen_refraccion', 'retino_eje', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.retino_av || ''} onChange={e => updateField('examen_refraccion', 'retino_av', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.querato_k1 || ''} onChange={e => updateField('examen_refraccion', 'querato_k1', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.querato_k2 || ''} onChange={e => updateField('examen_refraccion', 'querato_k2', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.querato_eje_k1 || ''} onChange={e => updateField('examen_refraccion', 'querato_eje_k1', e.target.value, 'od')} /></td>
                                <td><input value={refrac.od?.querato_eje_k2 || ''} onChange={e => updateField('examen_refraccion', 'querato_eje_k2', e.target.value, 'od')} /></td>
                            </tr>
                            <tr>
                                <td className="bg-yellow">OI</td>
                                <td><input value={refrac.oi?.autorefrac_esf || ''} onChange={e => updateField('examen_refraccion', 'autorefrac_esf', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.autorefrac_cil || ''} onChange={e => updateField('examen_refraccion', 'autorefrac_cil', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.autorefrac_eje || ''} onChange={e => updateField('examen_refraccion', 'autorefrac_eje', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.retino_esf || ''} onChange={e => updateField('examen_refraccion', 'retino_esf', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.retino_cil || ''} onChange={e => updateField('examen_refraccion', 'retino_cil', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.retino_eje || ''} onChange={e => updateField('examen_refraccion', 'retino_eje', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.retino_av || ''} onChange={e => updateField('examen_refraccion', 'retino_av', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.querato_k1 || ''} onChange={e => updateField('examen_refraccion', 'querato_k1', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.querato_k2 || ''} onChange={e => updateField('examen_refraccion', 'querato_k2', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.querato_eje_k1 || ''} onChange={e => updateField('examen_refraccion', 'querato_eje_k1', e.target.value, 'oi')} /></td>
                                <td><input value={refrac.oi?.querato_eje_k2 || ''} onChange={e => updateField('examen_refraccion', 'querato_eje_k2', e.target.value, 'oi')} /></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 4. PRESCRIPCIÓN ÓPTICA FINAL */}
                    <table>
                        <colgroup>
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '5.5%' }} />
                            <col style={{ width: '6.5%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '5.5%' }} />
                            <col style={{ width: '6.5%' }} />
                            <col style={{ width: '38%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th colSpan={11} className="bg-yellow" style={{ textAlign: 'left', paddingLeft: 6 }}>
                                    PRESCRIPCIÓN ÓPTICA FINAL
                                </th>
                            </tr>
                            <tr className="bg-yellow">
                                <th rowSpan={2}>OJOS</th>
                                <th colSpan={4}>LEJOS</th>
                                <th rowSpan={2}>ADICIÓN</th>
                                <th colSpan={4}>CERCA</th>
                                <th rowSpan={2} style={{ whiteSpace: 'nowrap' }}>OBSERVACIONES</th>
                            </tr>
                            <tr className="bg-yellow">
                                <th>ESF</th><th>CIL</th><th>EJE</th><th>AV</th>
                                <th>ESF</th><th>CIL</th><th>EJE</th><th>AV</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="bg-yellow">OD</td>
                                <td><input value={rx.od?.lejos_esf || ''} onChange={e => updateField('prescripcion_final', 'lejos_esf', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.lejos_cil || ''} onChange={e => updateField('prescripcion_final', 'lejos_cil', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.lejos_eje || ''} onChange={e => updateField('prescripcion_final', 'lejos_eje', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.lejos_av || ''} onChange={e => updateField('prescripcion_final', 'lejos_av', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.adicion || ''} onChange={e => updateField('prescripcion_final', 'adicion', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.cerca_esf || ''} onChange={e => updateField('prescripcion_final', 'cerca_esf', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.cerca_cil || ''} onChange={e => updateField('prescripcion_final', 'cerca_cil', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.cerca_eje || ''} onChange={e => updateField('prescripcion_final', 'cerca_eje', e.target.value, 'od')} /></td>
                                <td><input value={rx.od?.cerca_av || ''} onChange={e => updateField('prescripcion_final', 'cerca_av', e.target.value, 'od')} /></td>
                                <td rowSpan={2} style={{ verticalAlign: 'middle', padding: '2px 4px' }}>
                                    <input 
                                        value={rx.od?.observaciones || rx.oi?.observaciones || ''} 
                                        onChange={e => updateField('prescripcion_final', 'observaciones', e.target.value, 'od')} 
                                        placeholder="Observaciones de prescripción..."
                                        style={{ textAlign: 'center' }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="bg-yellow">OI</td>
                                <td><input value={rx.oi?.lejos_esf || ''} onChange={e => updateField('prescripcion_final', 'lejos_esf', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.lejos_cil || ''} onChange={e => updateField('prescripcion_final', 'lejos_cil', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.lejos_eje || ''} onChange={e => updateField('prescripcion_final', 'lejos_eje', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.lejos_av || ''} onChange={e => updateField('prescripcion_final', 'lejos_av', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.adicion || ''} onChange={e => updateField('prescripcion_final', 'adicion', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.cerca_esf || ''} onChange={e => updateField('prescripcion_final', 'cerca_esf', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.cerca_cil || ''} onChange={e => updateField('prescripcion_final', 'cerca_cil', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.cerca_eje || ''} onChange={e => updateField('prescripcion_final', 'cerca_eje', e.target.value, 'oi')} /></td>
                                <td><input value={rx.oi?.cerca_av || ''} onChange={e => updateField('prescripcion_final', 'cerca_av', e.target.value, 'oi')} /></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 5. DISTANCIA PUPILAR – PRESCRIPCION FINAL / PRISMA */}
                    <table>
                        <colgroup>
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '13.5%' }} />
                            <col style={{ width: '13.5%' }} />
                            <col style={{ width: '13.5%' }} />
                            <col style={{ width: '13.5%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '13.5%' }} />
                            <col style={{ width: '13.5%' }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-yellow">
                                <th rowSpan={2}>OJOS</th>
                                <th colSpan={5}>DISTANCIA PUPILAR – PRESCRIPCION FINAL</th>
                                <th colSpan={2}>PRISMA</th>
                            </tr>
                            <tr className="bg-yellow">
                                <th>NASO PUPILAR</th>
                                <th>ALTURA PUPILAR</th>
                                <th>DP LEJOS</th>
                                <th>DP CERCA</th>
                                <th>ALTURA BIFOCAL</th>
                                <th>HORIZONTAL</th>
                                <th>VERTICAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="bg-yellow">OD</td>
                                <td><input value={dp.od?.naso_pupilar || ''} onChange={e => updateField('dp_prisma', 'naso_pupilar', e.target.value, 'od')} /></td>
                                <td><input value={dp.od?.altura_pupilar || ''} onChange={e => updateField('dp_prisma', 'altura_pupilar', e.target.value, 'od')} /></td>
                                <td><input value={dp.od?.dp_lejos || ''} onChange={e => updateField('dp_prisma', 'dp_lejos', e.target.value, 'od')} placeholder="30/30" /></td>
                                <td><input value={dp.od?.dp_cerca || ''} onChange={e => updateField('dp_prisma', 'dp_cerca', e.target.value, 'od')} placeholder="30/30" /></td>
                                <td><input value={dp.od?.altura_bifocal || ''} onChange={e => updateField('dp_prisma', 'altura_bifocal', e.target.value, 'od')} /></td>
                                <td><input value={dp.od?.prisma_horizontal || ''} onChange={e => updateField('dp_prisma', 'prisma_horizontal', e.target.value, 'od')} /></td>
                                <td><input value={dp.od?.prisma_vertical || ''} onChange={e => updateField('dp_prisma', 'prisma_vertical', e.target.value, 'od')} /></td>
                            </tr>
                            <tr>
                                <td className="bg-yellow">OI</td>
                                <td><input value={dp.oi?.naso_pupilar || ''} onChange={e => updateField('dp_prisma', 'naso_pupilar', e.target.value, 'oi')} /></td>
                                <td><input value={dp.oi?.altura_pupilar || ''} onChange={e => updateField('dp_prisma', 'altura_pupilar', e.target.value, 'oi')} /></td>
                                <td><input value={dp.oi?.dp_lejos || ''} onChange={e => updateField('dp_prisma', 'dp_lejos', e.target.value, 'oi')} /></td>
                                <td><input value={dp.oi?.dp_cerca || ''} onChange={e => updateField('dp_prisma', 'dp_cerca', e.target.value, 'oi')} /></td>
                                <td><input value={dp.oi?.altura_bifocal || ''} onChange={e => updateField('dp_prisma', 'altura_bifocal', e.target.value, 'oi')} /></td>
                                <td><input value={dp.oi?.prisma_horizontal || ''} onChange={e => updateField('dp_prisma', 'prisma_horizontal', e.target.value, 'oi')} /></td>
                                <td><input value={dp.oi?.prisma_vertical || ''} onChange={e => updateField('dp_prisma', 'prisma_vertical', e.target.value, 'oi')} /></td>
                            </tr>
                            <tr>
                                <td colSpan={8} className="bg-yellow" style={{ textAlign: 'left', padding: '3px 8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <strong style={{ fontSize: '9px', whiteSpace: 'nowrap' }}>OBSERVACIONES:</strong>
                                        <input 
                                            value={dp.observaciones || ''} 
                                            onChange={e => updateField('dp_prisma', 'observaciones', e.target.value)} 
                                            placeholder="Observaciones generales del examen..."
                                            style={{ flex: 1, minWidth: 0, textAlign: 'left', fontWeight: 'bold' }}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={8} className="bg-yellow" style={{ textAlign: 'left', padding: '3px 8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <strong style={{ fontSize: '9px', whiteSpace: 'nowrap' }}>CONTROL:</strong>
                                        <input 
                                            value={dp.control || ''} 
                                            onChange={e => updateField('dp_prisma', 'control', e.target.value)} 
                                            placeholder="Pautas o periodicidad de control..."
                                            style={{ flex: 1, minWidth: 0, textAlign: 'left', fontWeight: 'bold' }}
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 6. CONTROL Y PROFESIONAL */}
                    <div className="sec-title" style={{ marginTop: 4 }}>CONTROL Y PROFESIONAL</div>
                    <table>
                        <colgroup>
                            <col style={{ width: '32%' }} />
                            <col style={{ width: '34%' }} />
                            <col style={{ width: '34%' }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-yellow">
                                <th>PRÓXIMO CONTROL</th>
                                <th>NOMBRE DEL PROFESIONAL</th>
                                <th>C.I. / FIRMA / SELLO</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ height: '44px' }}>
                                <td style={{ verticalAlign: 'top', paddingTop: 5 }}>
                                    <input 
                                        value={prof.proximo_control || ''} 
                                        onChange={e => updateField('control_profesional', 'proximo_control', e.target.value)} 
                                        placeholder="DD/MM/AAAA"
                                        style={{ fontWeight: 'bold' }}
                                    />
                                </td>
                                <td style={{ verticalAlign: 'top', paddingTop: 5 }}>
                                    <input 
                                        value={prof.nombre_profesional || ''} 
                                        onChange={e => updateField('control_profesional', 'nombre_profesional', e.target.value)} 
                                        placeholder="Nombre del profesional..."
                                        style={{ fontWeight: 'bold' }}
                                    />
                                    {prof.nombre_profesional && !prof.nombre_profesional.toLowerCase().includes('optometrista') && (
                                        <div style={{ fontSize: '8px', color: '#64748b', marginTop: 1 }}>Optometrista</div>
                                    )}
                                </td>
                                <td style={{ verticalAlign: 'bottom', paddingBottom: 4 }}>
                                    <div style={{ borderTop: '1px dotted #94a3b8', width: '80%', margin: '0 auto', paddingTop: 2, fontSize: '8px', color: '#64748b' }}>
                                        Firma y Sello
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <hr className="cert-divider" />

                    {/* Pie de página Oficial */}
                    <div className="cert-footer-info">
                        <div style={{ fontWeight: 'bold', fontSize: '9px', marginBottom: 2 }}>ÓPTICA VISIÓN CLICK</div>
                        <div>📍 <strong>Centro:</strong> Rafael Arellano y Ayacucho</div>
                        <div>📍 <strong>Sur:</strong> Av. Veintimilla y Camilo Ponce – Tulcán, Ecuador</div>
                        <div>📞 099 894 0200 | 099 338 7008</div>
                        <div>✉️ visionclic23@gmail.com</div>
                    </div>

                </div>
            </div>

            {/* ── Modal de Enviar por Correo (portal al body: siempre centrado sobre la pantalla) ── */}
            {modalCorreoAbierto && createPortal(
                <div
                    className="cv-modal-overlay"
                    onClick={e => { if (e.target === e.currentTarget && !enviandoCorreo) setModalCorreoAbierto(false); }}
                >
                        <div className="cv-modal-content">
                            <div className="cv-modal-header">
                                <h5>
                                    <i className="bi bi-envelope-paper-fill me-2 text-warning"></i>
                                    Enviar Certificado Visual por Correo
                                </h5>
                                <button
                                    type="button"
                                    className="cv-modal-close"
                                    onClick={() => setModalCorreoAbierto(false)}
                                    disabled={enviandoCorreo}
                                    aria-label="Cerrar"
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <form onSubmit={handleConfirmarEnvioCorreo}>
                                <div className="cv-modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold small text-secondary">Paciente Destinatario</label>
                                        <div className="p-2 bg-light rounded-3 border small fw-semibold text-dark">
                                            {docData.datos_paciente?.nombre_completo || pacienteSeleccionado?.first_name} (CI: {docData.datos_paciente?.identificacion || pacienteSeleccionado?.cedula})
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold small text-secondary">Correo Electrónico *</label>
                                        <input 
                                            type="email" 
                                            className="form-control" 
                                            required 
                                            value={correoDestino} 
                                            onChange={e => setCorreoDestino(e.target.value)}
                                            placeholder="ejemplo@correo.com"
                                        />
                                        <div className="form-text small">
                                            Se enviará a este correo con el Certificado Visual oficial adjunto en formato PDF.
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold small text-secondary">Asunto del Correo</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={asuntoCorreo} 
                                            onChange={e => setAsuntoCorreo(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold small text-secondary">Mensaje Adicional (Opcional)</label>
                                        <textarea 
                                            className="form-control" 
                                            rows={2} 
                                            value={mensajeExtra} 
                                            onChange={e => setMensajeExtra(e.target.value)}
                                            placeholder="Ej: Estimado paciente, adjuntamos su informe optométrico con la prescripción actualizada..."
                                        ></textarea>
                                    </div>

                                    <div className="p-2 bg-success-subtle text-success border border-success-subtle rounded-3 small d-flex align-items-center gap-2">
                                        <i className="bi bi-file-earmark-pdf-fill fs-5"></i>
                                        <div>
                                            <strong>Archivo adjunto automático:</strong>
                                            <div style={{ fontSize: '0.78rem' }}>
                                                Certificado_Visual_{(docData.datos_paciente?.nombre_completo || 'Paciente').replace(/[^a-zA-Z0-9_\-]/g, '_')}.pdf
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="cv-modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary rounded-pill px-3" 
                                        onClick={() => setModalCorreoAbierto(false)} 
                                        disabled={enviandoCorreo}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-success rounded-pill px-4 fw-bold" 
                                        disabled={enviandoCorreo}
                                    >
                                        {enviandoCorreo ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Enviando por Correo...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-send-fill me-2"></i>
                                                Enviar Ahora
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default CertificadoVisual;
